/**
 * ExecutionReportTab
 *
 * Shows pipeline throughput, step SLA compliance, and stalled cases.
 * Uses CSS-only bar charts (no external charting library required).
 */
import React, { useState, useCallback } from "react";
import { api } from "../../services/api.js";

interface StepDuration {
  step_id: string;
  avg_hours: number;
  count: number;
  timeout_count: number;
  compliance_pct: number;
}

interface StalledCase {
  case_id: string;
  current_step_id: string;
  lender_id: string;
  age_days: number;
  borrower_name: string;
}

const TH: React.CSSProperties = {
  padding: "10px 14px", textAlign: "left", fontWeight: 600,
  fontSize: 12, color: "var(--muted, #64748b)", textTransform: "uppercase",
  letterSpacing: "0.05em", borderBottom: "1px solid var(--border, #e2e8f0)",
  background: "var(--panel, #f8fafc)", whiteSpace: "nowrap",
};
const TD: React.CSSProperties = {
  padding: "10px 14px", fontSize: 14, color: "var(--ink, #0f172a)",
  borderBottom: "1px solid var(--border, #e2e8f0)",
};

function ComplianceBar({ pct }: { pct: number }): React.ReactElement {
  const color = pct >= 80 ? "#16a34a" : pct >= 50 ? "#d97706" : "#dc2626";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.5s ease" }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 36 }}>{pct}%</span>
    </div>
  );
}

function humanStep(id: string): string {
  const parts = id.replace("step_", "").split("_");
  const num = parts[0];
  const name = parts.slice(1).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  return `${num}. ${name}`;
}

export function ExecutionReportTab(): React.ReactElement {
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01`);
  const [to, setTo] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [steps, setSteps] = useState<StepDuration[]>([]);
  const [stalled, setStalled] = useState<StalledCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.reports.execution({ from, to });
      setSteps(res.data.step_durations as unknown as StepDuration[]);
      setStalled(res.data.stalled_cases as unknown as StalledCase[]);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 24, flexWrap: "wrap" }}>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>From</label>
          <input type="month" value={from} onChange={(e) => setFrom(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid var(--border, #e2e8f0)", borderRadius: 6, fontSize: 14 }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 4 }}>To</label>
          <input type="month" value={to} onChange={(e) => setTo(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid var(--border, #e2e8f0)", borderRadius: 6, fontSize: 14 }} />
        </div>
        <button
          id="btn-run-execution-report"
          onClick={() => { void fetch(); }}
          disabled={loading}
          style={{
            padding: "8px 20px", background: "var(--brand, #6366f1)", color: "#fff",
            border: "none", borderRadius: 6, cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 600, fontSize: 14, opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Loading…" : "Run Report"}
        </button>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: 14, color: "#991b1b", marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loaded && (
        <>
          {/* Stalled cases alert */}
          {stalled.length > 0 && (
            <div style={{ background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 8, padding: 16, marginBottom: 24 }}>
              <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 8 }}>
                ⚠ {stalled.length} Stalled Case{stalled.length > 1 ? "s" : ""} Detected
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ ...TH, background: "transparent", borderBottom: "1px solid #fbbf24" }}>Case ID</th>
                      <th style={{ ...TH, background: "transparent", borderBottom: "1px solid #fbbf24" }}>Borrower</th>
                      <th style={{ ...TH, background: "transparent", borderBottom: "1px solid #fbbf24" }}>Current Step</th>
                      <th style={{ ...TH, background: "transparent", borderBottom: "1px solid #fbbf24" }}>Age (Days)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stalled.map((c) => (
                      <tr key={c.case_id}>
                        <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{c.case_id}</td>
                        <td style={TD}>{c.borrower_name}</td>
                        <td style={TD}>{humanStep(c.current_step_id ?? "")}</td>
                        <td style={{ ...TD, fontWeight: 700, color: "#dc2626" }}>{c.age_days}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step SLA table */}
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", marginBottom: 14 }}>Step SLA Compliance</h3>
          {steps.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 8 }}>
              No completed step history data found for this period.
            </div>
          ) : (
            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid var(--border, #e2e8f0)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={TH}>Step</th>
                    <th style={TH}>Completions</th>
                    <th style={TH}>Avg. Duration (hrs)</th>
                    <th style={TH}>Timeouts</th>
                    <th style={{ ...TH, minWidth: 180 }}>SLA Compliance</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((s, i) => (
                    <tr key={s.step_id} style={{ background: i % 2 === 0 ? "#fff" : "var(--panel, #f8fafc)" }}>
                      <td style={TD}>{humanStep(s.step_id)}</td>
                      <td style={TD}>{s.count}</td>
                      <td style={TD}>{s.avg_hours}</td>
                      <td style={{ ...TD, color: s.timeout_count > 0 ? "#dc2626" : "var(--ink)" }}>
                        {s.timeout_count}
                      </td>
                      <td style={TD}><ComplianceBar pct={s.compliance_pct} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!loaded && !loading && (
        <div style={{ padding: 60, textAlign: "center", color: "var(--muted)" }}>
          Select a date range and click <strong>Run Report</strong> to view execution data.
        </div>
      )}
    </div>
  );
}
